import React from 'react';
import { Modal, Alert } from 'antd';

import {
  UserMessage,
  AIMessage,
  AIIntroduction,
  LoadingMessage,
  LoadMoreTrigger,
  MessageInputArea,
  ModalHeader
} from './components';

import { useChatLogic, useMessageState } from './hooks';
import { SalesAssistantModalProps, ChatItem } from './types';

import './salesAssistantModal.scss';

export const SalesAssistantModal: React.FC<SalesAssistantModalProps> = ({
  visible,
  onCancel,
  onSendMessage,
  contactName,
  chatHistory = [],
  chatLoading = false,
  chatError = '',
  getChatMessages,
  campaign_id,
  user_id,
  receiver_id,
  isChatHistoryLoading,
  chatHistoryMetaData,
}) => {
  // Custom hooks for logic separation
  const { containerRef, loadMoreRef, loadMore } = useChatLogic({
    visible,
    campaign_id,
    user_id,
    receiver_id,
    getChatMessages,
    chatHistory,
    isChatHistoryLoading,
    chatHistoryMetaData
  });

  const { message, setMessage, handleSendMessage, handleSuggestedAction } = useMessageState(onSendMessage);

  const renderChatMessage = (chatItem: ChatItem, index: number) => {
    const isUser = chatItem.type === 'user' || chatItem.query;
    const isAI = chatItem.type === 'ai' || chatItem.response;

    if (isUser) {
      return (
        <UserMessage
          key={index}
          message={chatItem.query || chatItem.message || ''}
          index={index}
        />
      );
    }

    if (isAI) {
      return (
        <AIMessage
          key={index}
          message={chatItem.response || chatItem.message || ''}
          index={index}
        />
      );
    }

    return null;
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      title={<ModalHeader contactName={contactName} />}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
      className="sales-assistant-modal"
    >
      <div className="modal-content">
        {/* Chat Messages Container with Infinite Scroll */}
        <div ref={containerRef} className="chat-container">
          {/* Loading Indicator */}
          {chatLoading && <LoadingMessage />}

          {/* Error Alert */}
          {chatError && (
            <Alert
              message="Error"
              description={chatError}
              type="error"
              showIcon
              className="error-alert"
            />
          )}

          {/* AI Introduction Message - only show if no chat history */}
          {chatHistory.length === 0 && !isChatHistoryLoading && (
            <AIIntroduction
              contactName={contactName}
              onSuggestedAction={handleSuggestedAction}
            />
          )}

          {/* Chat History */}
          {chatHistory.map((chatItem, index) => renderChatMessage(chatItem, index))}

          {/* Load More Trigger Div - This will be observed by Intersection Observer */}
          <LoadMoreTrigger
            hasNext={!!chatHistoryMetaData?.has_next}
            isLoading={isChatHistoryLoading}
            onLoadMore={loadMore}
            ref={loadMoreRef}
          />
        </div>

        {/* Message Input */}
        <MessageInputArea
          message={message}
          onMessageChange={setMessage}
          onSendMessage={handleSendMessage}
          isLoading={chatLoading}
        />
      </div>
    </Modal>
  );
};